import { MediaFileConnectionType, MediaFileSource, MediaFileUsedAs } from "@next-platform/packages-ts-types";
import { MediaFileKind } from "../types/types";
// A file can carry several uses, so the first match in this order names it.
const AVATAR_KIND_PRIORITY = [
    [MediaFileUsedAs.CHARACTER_AVATAR, MediaFileKind.CHARACTER_AVATAR],
    [MediaFileUsedAs.PERSONA_AVATAR, MediaFileKind.PERSONA_AVATAR],
    [MediaFileUsedAs.USER_AVATAR, MediaFileKind.USER_AVATAR],
    [MediaFileUsedAs.LOREBOOK_AVATAR, MediaFileKind.LOREBOOK_AVATAR],
];
/**
 * What a file is shown as, from its uses and source. Exported so a surface can pick an icon without labels.
 *
 * An avatar is named by what it is, however it arrived. A file with no recorded use is attached to nothing,
 * so it has no type yet. Any other uploaded file, today an image sent in a chat, reads `uploaded` rather than
 * as a conversation image.
 */
export function getMediaFileKind(file) {
    const avatar = AVATAR_KIND_PRIORITY.find(([usedAs]) => file.used_as.includes(usedAs));
    if (avatar) {
        return avatar[1];
    }
    if (file.used_as.length === 0) {
        return MediaFileKind.UNATTACHED;
    }
    return file.source === MediaFileSource.UPLOADED ? MediaFileKind.UPLOADED : MediaFileKind.CONVERSATION_IMAGE;
}
function getKindLabel(kind, translate) {
    switch (kind) {
        case MediaFileKind.UPLOADED:
            return translate("mediaFile:kind.uploaded");
        case MediaFileKind.CHARACTER_AVATAR:
            return translate("mediaFile:kind.characterAvatar");
        case MediaFileKind.PERSONA_AVATAR:
            return translate("mediaFile:kind.personaAvatar");
        case MediaFileKind.USER_AVATAR:
            return translate("mediaFile:kind.userAvatar");
        case MediaFileKind.LOREBOOK_AVATAR:
            return translate("mediaFile:kind.lorebookAvatar");
        case MediaFileKind.UNATTACHED:
            return translate("gallery:mediaFilePreview.noRelation");
        default:
            return translate("mediaFile:kind.conversationImage");
    }
}
/**
 * Whether anyone has judged this file, which is what makes `is_nsfw` worth reading. An unjudged file carries
 * `is_nsfw: false` because nobody looked, not because it is clean, so a surface that acts on the flag alone
 * speaks with a confidence the data does not have.
 */
export function isMediaFileModerated(file) {
    return file.is_auto_moderated || file.is_human_moderated;
}
// Older responses only expose owner usage connections; a supplied primary takes precedence.
function getCharacterId(file) {
    if (file.character !== undefined) {
        return file.character?.id ?? null;
    }
    if (!("connections" in file) || !Array.isArray(file.connections)) {
        return null;
    }
    return file.connections.find((connection) => connection.type === MediaFileConnectionType.CHARACTER)?.id ?? null;
}
// Created but not attached to a chatbot, persona or profile: the only state that reads "No relation". Unknown connections count as attached.
function isMediaFileUnattached(file) {
    if (file.used_as.length > 0 || file.character) {
        return false;
    }
    return "connections" in file && Array.isArray(file.connections) && file.connections.length === 0;
}
export default function getMediaFileMetadata(file, translate) {
    const kind = getMediaFileKind(file);
    return {
        id: file.id,
        imageUrl: file.url,
        width: file.width,
        height: file.height,
        kind,
        kindLabel: getKindLabel(kind, translate),
        isNsfw: file.is_nsfw,
        isModerated: isMediaFileModerated(file),
        characterId: getCharacterId(file),
        isUnattached: isMediaFileUnattached(file),
    };
}
